import { lazy, Suspense, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Sentry } from './lib/sentry';
import { axiom } from './lib/axiom';
import { ThemeProvider } from './contexts/ThemeContext';
import { WorkModeProvider } from './contexts/WorkModeContext';
import { LandingVariantProvider } from './contexts/LandingVariantContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { StreamsProvider, useStreams } from './contexts/StreamsContext';
// Note: StreamWebSocketProvider (legacy per-stream WS) removed in favor of
// UserWebSocketProvider (Hub-and-Spoke architecture) which handles all streams
// via a single persistent connection.
import { UserWebSocketProvider, useUserWebSocket } from './contexts/UserWebSocketContext';
import { ExecutionProvider } from './contexts/ExecutionContext';
import { AppErrorFallback } from './components/errors/AppErrorFallback';
import type { StreamEvent } from './types/api';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { Dashboard } from './pages/Dashboard';
import { SignIn } from './pages/SignIn';
import { Admin } from './pages/Admin';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { Settings } from './pages/Settings';
import { Reviews } from './pages/Reviews';
import { ReviewDebug } from './pages/ReviewDebug';
import { ReviewSettings } from './pages/ReviewSettings';
import { LandingPage } from './pages/LandingPage';

const Costs = lazy(() => import('./pages/Costs'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));

// Updates document title based on auth state
function TitleManager() {
  const { user, accessInfo } = useAuth();

  useEffect(() => {
    if (user && accessInfo?.hasAccess) {
      document.title = 'Cognitive Workflow Engine';
    } else {
      document.title = 'Demo';
    }
  }, [user, accessInfo]);

  return null;
}

// Manages stream subscriptions based on current stream selection
function StreamSubscriptionManager() {
  const { currentStreamData } = useStreams();
  const { subscribe, unsubscribe } = useUserWebSocket();
  const previousStreamIdRef = useRef<string | null>(null);

  useEffect(() => {
    const currentStreamId = currentStreamData?.id || null;
    const previousStreamId = previousStreamIdRef.current;

    // Unsubscribe from previous stream if changed
    if (previousStreamId && previousStreamId !== currentStreamId) {
      axiom.info(`[StreamSubscriptionManager] Unsubscribing from previous stream ${previousStreamId.slice(0, 8)}`);
      unsubscribe(previousStreamId);
    }

    // Subscribe to new stream
    if (currentStreamId && currentStreamId !== previousStreamId) {
      axiom.info(`[StreamSubscriptionManager] Subscribing to stream ${currentStreamId.slice(0, 8)}`);
      subscribe(currentStreamId).catch((err) => {
        axiom.error('[StreamSubscriptionManager] Subscribe failed', { error: err instanceof Error ? err.message : String(err) });
      });
    }

    previousStreamIdRef.current = currentStreamId;
  }, [currentStreamData?.id, subscribe, unsubscribe]);

  // Note: No reconnect effect needed - UserWebSocket.handleOpen() already
  // re-subscribes from the stored subscriptions map on reconnect.

  return null;
}

// Wrapper to connect UserWebSocket events to StreamsContext
function UserWebSocketWrapper({ children }: { children: React.ReactNode }) {
  const { handleWebSocketEvent, refreshCurrentStream } = useStreams();

  const handleStreamEvent = useCallback((streamId: string, event: StreamEvent) => {
    // Forward events to StreamsContext for the currently selected stream
    // Cross-stream events will be handled by attention updates
    handleWebSocketEvent(event);
  }, [handleWebSocketEvent]);

  const handleHardRefresh = useCallback((streamId: string) => {
    axiom.info('[App] Hard refresh required for stream', { stream_id: streamId });
    refreshCurrentStream().catch((err) => axiom.error('Error caught', { error: err instanceof Error ? err.message : String(err) }));
  }, [refreshCurrentStream]);

  return (
    <UserWebSocketProvider
      onStreamEvent={handleStreamEvent}
      onHardRefreshRequired={handleHardRefresh}
    >
      <StreamSubscriptionManager />
      {children}
    </UserWebSocketProvider>
  );
}

// Wrap Routes with Sentry for parameterized route tracking
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export default function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={AppErrorFallback}
      showDialog={true}
      dialogOptions={{
        title: 'Oops! Something went wrong',
        subtitle: 'Our team has been notified.',
        subtitle2: "If you'd like to help, tell us what happened below.",
      }}
    >
      <AuthProvider>
      <TitleManager />
      <ThemeProvider>
        <WorkModeProvider>
          <LandingVariantProvider>
            <StreamsProvider>
              <UserWebSocketWrapper>
                <ExecutionProvider>
                  <Router>
                    <SentryRoutes>
                      {/* Redirect root based on hostname: try. → onboarding, others → sign-in */}
                      <Route path="/" element={
                        <Navigate to={window.location.hostname.startsWith('try.') ? '/onboarding/welcome' : '/signin'} replace />
                      } />
                      <Route path="/signin" element={<SignIn />} />
                      <Route path="/auth/verify" element={<SignIn />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <Dashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/stream/:id"
                        element={
                          <ProtectedRoute>
                            <Dashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin"
                        element={
                          <ProtectedRoute requireAdmin>
                            <Admin />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reviews"
                        element={
                          <ProtectedRoute requireAdmin>
                            <Reviews />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reviews/settings"
                        element={
                          <ProtectedRoute requireAdmin>
                            <ReviewSettings />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reviews/:sessionId/debug"
                        element={
                          <ProtectedRoute requireAdmin>
                            <ReviewDebug />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/costs"
                        element={
                          <ProtectedRoute requireAdmin>
                            <Suspense fallback={null}>
                              <Costs />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/settings"
                        element={
                          <ProtectedRoute>
                            <Settings />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/landing"
                        element={
                          <ProtectedRoute>
                            <LandingPage />
                          </ProtectedRoute>
                        }
                      />
                      {/* Onboarding prototype — public, no auth required */}
                      <Route
                        path="/onboarding/*"
                        element={
                          <Suspense fallback={null}>
                            <OnboardingPage />
                          </Suspense>
                        }
                      />
                      {/* Catch-all: redirect unknown routes to sign-in */}
                      <Route path="*" element={<Navigate to="/signin" replace />} />
                    </SentryRoutes>
                  </Router>
                </ExecutionProvider>
              </UserWebSocketWrapper>
            </StreamsProvider>
          </LandingVariantProvider>
        </WorkModeProvider>
      </ThemeProvider>
    </AuthProvider>
    </Sentry.ErrorBoundary>
  );
}
